<template>
  <div class="pb-10">
    <PageTitle title="Gestion des clients" :description="`${pagination.total || 0} client(s) au total`">
      <template #actions>
        <button v-permission="'customers'"
          class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="downloadUnpaidList" :disabled="isDownloading">
          <i v-if="isDownloading" class="fas fa-spinner fa-spin text-xs"></i>
          <i v-else class="fas fa-file-arrow-down text-xs"></i>
          Liste impayés
        </button>
        <button v-permission="'customers.create'"
          class="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
          @click="onAddCustomer">
          <i class="fas fa-plus text-xs"></i>
          Nouveau client
        </button>
      </template>
    </PageTitle>

    <!-- Filtres -->
    <BaseCard class="mb-5">
      <div class="flex flex-wrap gap-4 items-end">
        <!-- Recherche -->
        <div class="flex-1 min-w-[250px]">
          <BaseInput v-model="searchQuery" type="search" placeholder="Rechercher un client (nom, téléphone, email)..."
            clearable @input="onSearchInput">
            <template #icon-left>
              <i class="fa-solid fa-search"></i>
            </template>
          </BaseInput>
        </div>

        <!-- Filtre impayés -->
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors"
          :class="showUnpaidOnly
            ? 'bg-primary text-white border-primary hover:bg-primary-dark'
            : 'text-gray-600 border-gray-200 hover:bg-gray-50'"
          @click="toggleUnpaidFilter">
          <i class="fas fa-triangle-exclamation text-[11px]"></i>
          {{ showUnpaidOnly ? 'Tous les clients' : 'Clients avec impayés' }}
        </button>
      </div>
    </BaseCard>

    <!-- Filtres actifs -->
    <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mb-5">
      <span class="text-xs text-gray-400">Filtres:</span>
      <span v-if="searchQuery"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
        "{{ searchQuery }}"
        <button @click="clearSearch" class="hover:text-blue-900 transition-colors">
          <i class="fas fa-times text-[9px]"></i>
        </button>
      </span>
      <span v-if="showUnpaidOnly"
        class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
        Impayés
        <button @click="toggleUnpaidFilter" class="hover:text-orange-900 transition-colors">
          <i class="fas fa-times text-[9px]"></i>
        </button>
      </span>
      <button class="text-xs text-gray-400 hover:text-gray-600 ml-auto transition-colors" @click="clearAllFilters">
        Tout effacer
      </button>
    </div>

    <!-- Tableau des clients -->
    <BaseCard padding="none">
      <!-- Chargement -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16">
        <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
        <p class="text-sm text-gray-400">Chargement des clients...</p>
      </div>

      <!-- État vide -->
      <div v-else-if="customers.length === 0"
        class="flex flex-col items-center justify-center py-16 text-center px-6">
        <div class="text-gray-300 mb-4">
          <i class="fas fa-users text-5xl"></i>
        </div>
        <h3 class="text-base font-semibold text-gray-700 mb-1">Aucun client trouvé</h3>
        <p class="text-sm text-gray-400 max-w-sm mb-6">
          <span v-if="hasActiveFilters">Aucun client ne correspond à vos critères de recherche.</span>
          <span v-else>Commencez par ajouter votre premier client.</span>
        </p>
        <button v-if="hasActiveFilters"
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
          @click="clearAllFilters">
          Réinitialiser les filtres
        </button>
        <button v-else
          class="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
          @click="onAddCustomer">
          <i class="fas fa-plus text-xs"></i>
          Ajouter un client
        </button>
      </div>

      <!-- Tableau -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50/80 border-b border-gray-200">
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Ventes
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Impayés
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Créé le
              </th>
              <th class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="customer in customers" :key="customer.id" class="hover:bg-gray-50/80 transition-colors">
              <!-- Client -->
              <td class="px-5 py-3.5">
                <div class="flex items-center gap-3 cursor-pointer group" @click="viewCustomer(customer)">
                  <div
                    class="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                    <i class="fas fa-user text-primary text-[10px]"></i>
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors truncate uppercase">
                      {{ customer.name }}
                    </p>
                    <p v-if="customer.company" class="text-xs text-gray-400 truncate">{{ customer.company }}</p>
                  </div>
                </div>
              </td>
              <!-- Contact -->
              <td class="px-5 py-3.5">
                <div v-if="customer.phone || customer.email" class="space-y-0.5">
                  <p v-if="customer.phone" class="text-sm text-gray-700 flex items-center gap-1.5">
                    <i class="fas fa-phone text-[9px] text-gray-300"></i>
                    {{ customer.phone }}
                  </p>
                  <p v-if="customer.email" class="text-xs text-gray-400 flex items-center gap-1.5 truncate">
                    <i class="fas fa-envelope text-[9px] text-gray-300"></i>
                    {{ customer.email }}
                  </p>
                </div>
                <span v-else class="text-xs text-gray-300 italic">Aucun contact</span>
              </td>
              <!-- Ventes -->
              <td class="px-5 py-3.5">
                <span class="text-sm font-bold text-gray-900">{{ formatCurrency(customer.total_spent || 0) }}</span>
              </td>
              <!-- Impayés -->
              <td class="px-5 py-3.5">
                <div class="space-y-0.5">
                  <p class="text-sm font-semibold" :class="customer.total_unpaid > 0 ? 'text-orange-600' : 'text-gray-300'">
                    {{ formatCurrency(customer.total_unpaid || 0) }}
                  </p>
                  <p class="text-xs text-gray-400">
                    {{ customer.unpaid_sales_count || 0 }} facture(s)
                  </p>
                </div>
              </td>
              <!-- Statut -->
              <td class="px-5 py-3.5">
                <span :class="[
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold',
                  customer.status ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                ]">
                  <span class="w-1.5 h-1.5 rounded-full" :class="customer.status ? 'bg-green-500' : 'bg-gray-400'"></span>
                  {{ customer.status ? 'Actif' : 'Inactif' }}
                </span>
              </td>
              <!-- Créé le -->
              <td class="px-5 py-3.5 text-sm text-gray-500">
                {{ formatDate(customer.created_at) }}
              </td>
              <!-- Actions -->
              <td class="px-5 py-3.5">
                <div class="flex items-center justify-end gap-1">
                  <button
                    class="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                    title="Voir le client" @click="viewCustomer(customer)">
                    <i class="fas fa-eye text-sm"></i>
                  </button>
                  <button
                    class="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                    title="Modifier" @click="editCustomer(customer)">
                    <i class="fas fa-pen text-sm"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && customers.length > 0" class="border-t border-gray-100 px-5 py-4">
        <PaginationComponent :current-page="pagination.currentPage" :per-page="pagination.perPage"
          :total="pagination.total" :from="pagination.from" :to="pagination.to" :show-page-numbers="true"
          :max-visible-pages="5" @page-change="goToPage" />
      </div>
    </BaseCard>

    <!-- Modal de création -->
    <CreateCustomerModal :show="showCreateModal" @close="closeCreateModal" @created="onCustomerCreated" />

    <!-- Modal d'édition -->
    <CustomerEditModal :show="showEditModal" :customer="selectedCustomer" @close="closeEditModal"
      @updated="onCustomerUpdated" />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import customerService from '@/services/customerService'
import PageTitle from '@/components/common/PageTitle.vue'
import BaseCard from '@/components/base/BaseCard.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import CustomerEditModal from '@/components/customers/CustomerEditModal.vue'
import CreateCustomerModal from '@/components/customers/CreateCustomerModal.vue'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'CustomerList',
  components: {
    PageTitle,
    BaseCard,
    BaseInput,
    PaginationComponent,
    CustomerEditModal,
    CreateCustomerModal
  },
  setup() {
    const router = useRouter()
    const toastStore = useToastStore()

    // État
    const customers = ref([])
    const loading = ref(true)
    const isDownloading = ref(false)
    const searchQuery = ref('')
    const searchTimeout = ref(null)
    const showUnpaidOnly = ref(false)

    // Modal d'édition
    const showEditModal = ref(false)
    const selectedCustomer = ref(null)

    // Modal de création
    const showCreateModal = ref(false)

    // Pagination
    const pagination = ref({
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      from: 0,
      to: 0
    })

    // Propriétés calculées
    const hasActiveFilters = computed(() => {
      return searchQuery.value || showUnpaidOnly.value
    })

    // Méthodes de chargement
    const loadCustomers = async (params = {}) => {
      try {
        loading.value = true
        const response = await customerService.getCustomers({
          page: pagination.value.currentPage,
          per_page: pagination.value.perPage,
          search: searchQuery.value,
          with_unpaid: showUnpaidOnly.value,
          ...params
        })

        const data = response.data
        customers.value = data.data || []

        if (data.meta) {
          const meta = data.meta
          const perPage = meta.per_page || pagination.value.perPage
          const total = meta.total || 0
          const calculatedLastPage = perPage > 0 ? Math.ceil(total / perPage) : 1

          pagination.value = {
            currentPage: pagination.value.currentPage,
            perPage: perPage,
            total: total,
            lastPage: calculatedLastPage > 0 ? calculatedLastPage : 1,
            from: meta.from || 0,
            to: meta.to || 0
          }
        } else {
          pagination.value.total = customers.value.length
          pagination.value.from = 1
          pagination.value.to = customers.value.length
          pagination.value.lastPage = 1
        }
      } catch (error) {
        console.error('Erreur lors du chargement des clients:', error)
        customers.value = []
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement des clients',
          duration: 4000
        })
      } finally {
        loading.value = false
      }
    }

    // Méthodes de recherche
    const onSearchInput = () => {
      clearTimeout(searchTimeout.value)
      searchTimeout.value = setTimeout(() => {
        pagination.value.currentPage = 1
        loadCustomers()
      }, 500)
    }

    const clearSearch = () => {
      searchQuery.value = ''
      pagination.value.currentPage = 1
      loadCustomers()
    }

    const clearAllFilters = () => {
      searchQuery.value = ''
      showUnpaidOnly.value = false
      pagination.value.currentPage = 1
      loadCustomers()
    }

    const toggleUnpaidFilter = () => {
      showUnpaidOnly.value = !showUnpaidOnly.value
      pagination.value.currentPage = 1
      loadCustomers()
    }

    // Méthodes de pagination
    const goToPage = (page) => {
      const lastPage = pagination.value.lastPage || 1
      if (page < 1 || (lastPage > 0 && page > lastPage)) return
      pagination.value.currentPage = page
      loadCustomers()
    }

    // Méthodes d'actions
    const onAddCustomer = () => {
      showCreateModal.value = true
    }

    const viewCustomer = (customer) => {
      router.push({ name: 'customer-detail', params: { id: customer.id } })
    }

    const editCustomer = (customer) => {
      selectedCustomer.value = customer
      showEditModal.value = true
    }

    const closeEditModal = () => {
      showEditModal.value = false
      selectedCustomer.value = null
    }

    const onCustomerUpdated = (updatedCustomer) => {
      const index = customers.value.findIndex(c => c.id === updatedCustomer.id)
      if (index !== -1) {
        customers.value[index] = { ...customers.value[index], ...updatedCustomer }
      }
    }

    const onCustomerCreated = () => {
      loadCustomers()
      showCreateModal.value = false
    }

    const closeCreateModal = () => {
      showCreateModal.value = false
    }

    // Méthodes utilitaires
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR')
    }

    const downloadUnpaidList = async () => {
      if (isDownloading.value) return

      isDownloading.value = true
      try {
        await customerService.downloadUnpaidList(false)
      } catch (error) {
        console.error('Erreur lors du téléchargement de la liste des impayés:', error)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du téléchargement. Veuillez réessayer.',
          duration: 4000
        })
      } finally {
        isDownloading.value = false
      }
    }

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
      }
    })

    onMounted(() => {
      loadCustomers()
    })

    return {
      customers,
      loading,
      isDownloading,
      searchQuery,
      showUnpaidOnly,
      pagination,
      showEditModal,
      selectedCustomer,
      showCreateModal,
      hasActiveFilters,
      loadCustomers,
      onSearchInput,
      clearSearch,
      clearAllFilters,
      toggleUnpaidFilter,
      goToPage,
      onAddCustomer,
      viewCustomer,
      editCustomer,
      closeEditModal,
      onCustomerUpdated,
      onCustomerCreated,
      closeCreateModal,
      formatCurrency,
      formatDate,
      downloadUnpaidList
    }
  }
})
</script>
